import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, Mail, MessageSquare, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { commLogAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

export default function CommunicationLogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState('all');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await commLogAPI.list({ channel, q: q || undefined, limit: 200 });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load the log'));
    } finally {
      setLoading(false);
    }
  }, [channel, q]);

  useEffect(() => { load(); }, [channel]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="communication-log-page">
      <PageHeader
        title="Communication Log"
        description="Every email and SMS sent from KR Agency Hub, in one place."
        breadcrumbs={[{ label: 'Communication Log', href: '/communication-log' }]}
      />
      <div className="page-content space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="Search number, email, subject, message…"
              className="pl-9 h-9 text-sm"
              data-testid="comm-log-search"
            />
          </div>
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger className="h-9 w-40 text-xs" data-testid="comm-log-channel"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={load} data-testid="comm-log-search-btn">Search</Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <Card><CardContent className="py-14 text-center text-sm text-slate-500" data-testid="comm-log-empty">No communications found.</CardContent></Card>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="comm-log-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Channel</th>
                  <th className="px-3 py-2 text-left">Dir</th>
                  <th className="px-3 py-2 text-left">To / From</th>
                  <th className="px-3 py-2 text-left">Subject / Message</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((m, i) => (
                  <tr
                    key={i}
                    className="hover:bg-slate-50 cursor-pointer"
                    data-testid={`comm-log-row-${i}`}
                    onClick={() => setDetail(m)}
                  >
                    <td className="px-3 py-2">
                      {m.channel === 'email'
                        ? <Badge className="bg-sky-100 text-sky-800"><Mail size={11} className="mr-1" /> Email</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-800"><MessageSquare size={11} className="mr-1" /> SMS</Badge>}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{m.direction || '—'}</td>
                    <td className="px-3 py-2 text-xs font-mono">{m.to || m.from || '—'}</td>
                    <td className="px-3 py-2 text-xs max-w-[360px] truncate" title={m.subject || m.body || ''}>{m.subject || m.body || '—'}</td>
                    <td className="px-3 py-2 text-xs">{m.status || '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{m.at ? new Date(m.at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="comm-log-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detail?.channel === 'email'
                ? <Mail size={16} className="text-sky-600" />
                : <MessageSquare size={16} className="text-emerald-600" />}
              {detail?.channel === 'email'
                ? (detail?.subject || '(no subject)')
                : `SMS ${detail?.direction === 'inbound' ? 'received' : 'sent'}`}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {detail?.at ? new Date(detail.at).toLocaleString() : ''}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="comm-log-detail-meta">
                {detail.from && <div><span className="text-slate-500">From:</span> <span className="font-mono">{detail.from}</span></div>}
                {detail.to && <div><span className="text-slate-500">To:</span> <span className="font-mono">{detail.to}</span></div>}
                <div><span className="text-slate-500">Direction:</span> {detail.direction || '—'}</div>
                <div><span className="text-slate-500">Status:</span> {detail.status || '—'}</div>
              </div>
              {detail.channel === 'email' ? (
                detail.body_html ? (
                  <iframe
                    title="email-body"
                    srcDoc={detail.body_html}
                    className="w-full rounded-md border border-slate-200 bg-white"
                    style={{ height: 420 }}
                    sandbox=""
                    data-testid="comm-log-email-body"
                  />
                ) : (
                  <div className="rounded-md border border-dashed border-slate-300 p-4 text-xs text-slate-500" data-testid="comm-log-no-body">
                    The full message body wasn't captured for this email (it was
                    sent before body logging was enabled). Newer emails will show
                    their full content here.
                  </div>
                )
              ) : (
                <div className="rounded-md border border-slate-200 bg-white p-4 whitespace-pre-wrap text-slate-800" data-testid="comm-log-sms-body">
                  {detail.body || <span className="text-slate-400">(no message content)</span>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
